import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import {
  AgencyModel,
  defaultTeamModel,
  ProductModel,
  UserModel,
  AllHostsList,
  BunchCategoryModel,
  DenmarkHostModel,
} from "@/database";

// import { UserListAction } from './constants.action';

interface MediaLogsListState {
  list: any[];
  count: number;
  isLoading: boolean;
  error: string;
  userContributorData: UserModel[];
}

const initialState = {
  businessList: {
    list: [] as Array<UserModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  bunchBusinessList: {
    list: [] as Array<UserModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  bunchProductList: {
    list: [] as Array<ProductModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  bunchAllProductList: {
    list: [] as Array<UserModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  bunchAllBusinessList: {
    list: [] as Array<UserModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  bunchCategoryList: {
    list: [] as Array<BunchCategoryModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  allHostsList: {
    list: {} as AllHostsList,
    isLoading: true,
    error: "",
  },
  denmarkHostsList: {
    list: [] as Array<DenmarkHostModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  defaultTeam: {} as defaultTeamModel,
  defaultBusiness: {} as defaultTeamModel,
  bunchLanguageList: {
    list: [] as Array<BunchCategoryModel>,
    count: 0,
    isLoading: true,
    error: "",
  },

  mediaLogsList: {
    list: [] as any[],
    count: 0,
    isLoading: true,
    error: "",
    userContributorData: [] as UserModel[],
  } as MediaLogsListState,
  currentBusiness: null as any,
  business: [] as UserModel[],
  bunchBusinessProductMap: {} as any,
  defaultTeamFromConstants: {} as any,
  defaultBusinessFromConstants: {} as any,
  productList: {
    list: [] as Array<any>,
    count: 0,
    isLoading: true,
    error: "",
  },
  currentProduct: null as any,
  product: [] as UserModel[],
  agenciesList: {
    list: [] as Array<AgencyModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  bunchAgenciesList: {
    list: [] as Array<UserModel>,
    count: 0,
    isLoading: true,
    error: "",
  },
  currentAgency: null as UserModel | null,
  agency: [] as UserModel[],
  organizationList: {
    list: [] as Array<any>,
    count: 0,
    isLoading: true,
    error: "",
  },
  currentOrganization: null as UserModel | null,
  organization: [] as UserModel[],
};
const deepMerge = (obj1: any, obj2: any) => {
  const merged = { ...obj1 };
  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(obj2, key)) {
      if (
        typeof obj2[key] === "object" &&
        obj2[key] !== null &&
        !Array.isArray(obj2[key]) &&
        typeof obj1[key] === "object"
      ) {
        merged[key] = deepMerge(obj1[key], obj2[key]);
      } else {
        merged[key] = obj2[key];
      }
    }
  }

  return merged;
};

function updateItemInList<T extends { id: string }>(list: T[], data: T): T[] {
  const index = list.findIndex((item) => item.id === data.id);
  if (index !== -1) {
    list[index] = { ...list[index], ...data };
  }
  return list;
}
const ConstantSlice = createSlice({
  name: "constantSlice",
  initialState,
  reducers: {
    setBusinessList: (state, { payload }: PayloadAction<any>) => {
      state.businessList = {
        ...state.businessList,
        error: payload?.data?.error ?? state.businessList.error,
        list: payload?.data?.list ?? state.businessList.list,
        count: payload?.data?.count ?? state.businessList.count,
        isLoading: payload?.data?.isLoading ?? state.businessList.isLoading,
      };
    },
    setCurrentBusiness: (
      state,
      { payload }: PayloadAction<UserModel | null>
    ) => {
      state.currentBusiness = payload;
      if (payload) {
        const index = state.businessList.list.findIndex(
          (business) => business.id === payload.id
        );
        if (index !== -1) {
          state.businessList.list = state.businessList.list.map(
            (business, i) =>
              i === index ? { ...business, ...payload } : business
          );
        }
      }
    },
    addBusinessNew: (state, action) => {
      const business = action.payload;
      state.businessList.list = [business, ...state.businessList.list];
    },
    setAllHostsList: (state, { payload }: PayloadAction<any>) => {
      state.allHostsList = {
        ...state.allHostsList,
        error: payload?.data?.error ?? state.allHostsList.error,
        list: payload?.data?.list ?? state.allHostsList.list,
        isLoading: payload?.data?.isLoading ?? state.allHostsList.isLoading,
      };
    },
    setDenmarkHostsList: (state, { payload }: PayloadAction<any>) => {
      state.denmarkHostsList = {
        ...state.denmarkHostsList,
        error: payload?.data?.error ?? state.denmarkHostsList.error,
        list: payload?.data?.list ?? state.denmarkHostsList.list,
        count: payload?.data?.count ?? state.denmarkHostsList.count,
        isLoading: payload?.data?.isLoading ?? state.denmarkHostsList.isLoading,
      };
    },
    setProductList: (state, { payload }: PayloadAction<any>) => {
      state.productList = {
        ...state.productList,
        error: payload?.data?.error ?? state.productList.error,
        list: payload?.data?.list ?? state.productList.list,
        count: payload?.data?.count ?? state.productList.count,
        isLoading: payload?.data?.isLoading ?? state.productList.isLoading,
      };
    },
    setBunchBusinessList: (state, { payload }: PayloadAction<any>) => {
      state.bunchBusinessList = {
        ...state.bunchBusinessList,
        error: payload?.data?.error ?? state.bunchBusinessList.error,
        list: payload?.data?.list ?? state.bunchBusinessList.list,
        count: payload?.data?.count ?? state.bunchBusinessList.count,
        isLoading:
          payload?.data?.isLoading ?? state.bunchBusinessList.isLoading,
      };
    },
    setBunchProductList: (state, { payload }: PayloadAction<any>) => {
      state.bunchProductList = {
        ...state.bunchProductList,
        error: payload?.data?.error ?? state.bunchProductList.error,
        list: payload?.data?.list ?? state.bunchProductList.list,
        count: payload?.data?.count ?? state.bunchProductList.count,
        isLoading: payload?.data?.isLoading ?? state.bunchProductList.isLoading,
      };
    },
    setBunchAllProductList: (state, { payload }: PayloadAction<any>) => {
      state.bunchAllProductList = {
        ...state.bunchAllProductList,
        error: payload?.data?.error ?? state.bunchAllProductList.error,
        list: payload?.data?.list ?? state.bunchAllProductList.list,
        count: payload?.data?.count ?? state.bunchAllProductList.count,
        isLoading:
          payload?.data?.isLoading ?? state.bunchAllProductList.isLoading,
      };
    },
    setBunchAllBusinessList: (state, { payload }: PayloadAction<any>) => {
      state.bunchAllBusinessList = {
        ...state.bunchAllBusinessList,
        error: payload?.data?.error ?? state.bunchAllBusinessList.error,
        list: payload?.data?.list ?? state.bunchAllBusinessList.list,
        count: payload?.data?.count ?? state.bunchAllBusinessList.count,
        isLoading:
          payload?.data?.isLoading ?? state.bunchAllBusinessList.isLoading,
      };
    },
    setBunchCategoryList: (state, { payload }: PayloadAction<any>) => {
      state.bunchCategoryList = {
        ...state.bunchCategoryList,
        error: payload?.data?.error ?? state.bunchCategoryList.error,
        list: payload?.data?.list ?? state.bunchCategoryList.list,
        count: payload?.data?.count ?? state.bunchCategoryList.count,
        isLoading:
          payload?.data?.isLoading ?? state.bunchCategoryList.isLoading,
      };
    },
    setBunchLanguageList: (state, { payload }: PayloadAction<any>) => {
      state.bunchLanguageList = {
        ...state.bunchLanguageList,
        error: payload?.data?.error ?? state.bunchLanguageList.error,
        list: payload?.data?.list ?? state.bunchLanguageList.list,
        count: payload?.data?.count ?? state.bunchLanguageList.count,
        isLoading:
          payload?.data?.isLoading ?? state.bunchLanguageList.isLoading,
      };
    },
    setBunchBusinessProductMap: (state, { payload }: PayloadAction<any>) => {
      state.bunchBusinessProductMap = payload;
    },
    setDefaultTeamFromConstants: (state, { payload }: PayloadAction<any>) => {
      state.defaultTeamFromConstants = payload;
    },
    setDefaultBusinessFromConstants: (
      state,
      { payload }: PayloadAction<any>
    ) => {
      state.defaultBusinessFromConstants = payload;
    },
    setBunchAgenciesList: (state, { payload }: PayloadAction<any>) => {
      state.bunchAgenciesList = {
        ...state.bunchAgenciesList,
        error: payload?.data?.error ?? state.bunchAgenciesList.error,
        list: payload?.data?.list ?? state.bunchAgenciesList.list,
        count: payload?.data?.count ?? state.bunchAgenciesList.count,
        isLoading:
          payload?.data?.isLoading ?? state.bunchAgenciesList.isLoading,
      };
    },
    setCurrentProduct: (
      state,
      { payload }: PayloadAction<UserModel | null>
    ) => {
      state.currentProduct = payload;
      if (payload) {
        const index = state.productList.list.findIndex(
          (product) => product.id === payload.id
        );
        if (index !== -1) {
          state.productList.list = state.productList.list.map((product, i) => {
            const updatedProduct =
              i === index ? deepMerge(product, payload) : product;
            return updatedProduct;
          });
        }
      }
    },
    addProductNew: (state, action) => {
      const product = action.payload;
      state.productList.list = [product, ...state.productList.list];
    },

    setAgenciesList: (state, { payload }: PayloadAction<any>) => {
      state.agenciesList = {
        ...state.agenciesList,
        error: payload?.data?.error ?? state.agenciesList.error,
        list: payload?.data?.list ?? state.agenciesList.list,
        count: payload?.data?.count ?? state.agenciesList.count,
        isLoading: payload?.data?.isLoading ?? state.agenciesList.isLoading,
      };
    },
    setCurrentAgency: (state, { payload }: PayloadAction<UserModel | null>) => {
      state.currentAgency = payload;
      if (payload) {
        const index = state.agenciesList.list.findIndex(
          (agencies) => agencies.id === payload.id
        );
        if (index !== -1) {
          state.agenciesList.list = state.agenciesList.list.map(
            (agencies, i) =>
              i === index ? deepMerge(agencies, payload) : agencies
          );
        }
      }
    },
    setDefaultTeam: (state, { payload }: PayloadAction<defaultTeamModel>) => {
      state.defaultTeam = payload;
    },
    setDefaultBusiness: (
      state,
      { payload }: PayloadAction<defaultTeamModel>
    ) => {
      state.defaultBusiness = payload;
    },
    addAgenciesNew: (state, action) => {
      const agencies = action.payload;
      state.agenciesList.list = [agencies, ...state.agenciesList.list];
    },

    setOrganizationList: (state, { payload }: PayloadAction<any>) => {
      state.organizationList = {
        ...state.organizationList,
        error: payload?.data?.error ?? state.organizationList.error,
        list: payload?.data?.list ?? state.organizationList.list,
        count: payload?.data?.count ?? state.organizationList.count,
        isLoading: payload?.data?.isLoading ?? state.organizationList.isLoading,
      };
    },
    setCurrentOrganization: (
      state,
      { payload }: PayloadAction<UserModel | null>
    ) => {
      state.currentOrganization = payload;
      if (payload) {
        const index = state.organizationList.list.findIndex(
          (organization) => organization.id === payload.id
        );
        if (index !== -1) {
          state.organizationList.list = state.organizationList.list.map(
            (organization, i) =>
              i === index ? { ...organization, ...payload } : organization
          );
        }
      }
    },
    setMediaLogsList: (
      state,
      { payload }: PayloadAction<{ data: MediaLogsListState }>
    ) => {
      state.mediaLogsList = {
        ...state.mediaLogsList,
        isLoading: payload?.data?.isLoading ?? state.mediaLogsList.isLoading,
        error: payload?.data?.error ?? state.mediaLogsList.error,
        list: payload?.data?.list ?? state.mediaLogsList.list,
        count: payload?.data?.count ?? state.mediaLogsList.count,
        userContributorData:
          payload?.data?.userContributorData ??
          state.mediaLogsList.userContributorData,
      };
    },
    addOrganizationNew: (state, action) => {
      const organization = action.payload;
      state.organizationList.list = [
        organization,
        ...state.organizationList.list,
      ];
    },
    updateBusinessInBusinessList: (
      state,
      { payload }: PayloadAction<{ data: any }>
    ) => {
      state.businessList.list = updateItemInList(
        state.businessList.list,
        payload.data
      );
      if (state.currentBusiness?.id === payload.data.id) {
        state.currentBusiness = { ...state.currentBusiness, ...payload.data };
      }
    },
    updateProductInProductList: (
      state,
      { payload }: PayloadAction<{ data: any }>
    ) => {
      state.productList.list = updateItemInList(
        state.productList.list,
        payload.data
      );
      if (state.currentProduct?.id === payload.data.id) {
        state.currentProduct = { ...state.currentProduct, ...payload.data };
      }
    },
  },
});

export const {
  setBusinessList,
  setCurrentBusiness,
  addBusinessNew,
  setProductList,
  setCurrentProduct,
  addProductNew,
  setAgenciesList,
  setCurrentAgency,
  addAgenciesNew,
  setOrganizationList,
  setCurrentOrganization,
  addOrganizationNew,
  updateProductInProductList,
  setMediaLogsList,
  updateBusinessInBusinessList,
  setBunchBusinessList,
  setBunchProductList,
  setBunchCategoryList,
  setBunchLanguageList,
  setBunchBusinessProductMap,
  setDefaultTeamFromConstants,
  setDefaultBusinessFromConstants,
  setBunchAgenciesList,
  setBunchAllProductList,
  setBunchAllBusinessList,
  setDefaultTeam,
  setAllHostsList,
  setDenmarkHostsList,
  setDefaultBusiness,
} = ConstantSlice.actions;

export default ConstantSlice.reducer;
